"use client";

import { useState, useEffect } from "react";
import { expressInterest } from "@/app/actions/jobs";

type PriceType = "fixed" | "range";

export function InterestButton({
  jobId,
  alreadyInterested,
}: {
  jobId: string;
  alreadyInterested: boolean;
}) {
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState("");
  const [priceType, setPriceType] = useState<PriceType>("fixed");
  const [priceLow, setPriceLow] = useState("");
  const [priceHigh, setPriceHigh] = useState("");
  const [duration, setDuration] = useState("");
  const [startDate, setStartDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(alreadyInterested);

  const [tomorrow, setTomorrow] = useState("");

  // Escape collapses the form while it is open.
  useEffect(() => {
    if (!showForm) return;
    function handleEscape(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") setShowForm(false);
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showForm]);

  async function handleSubmit() {
    if (!message.trim()) {
      setError("Please write a cover message.");
      return;
    }
    if (message.trim().length < 20) {
      setError("Please write at least 20 characters in your message.");
      return;
    }

    const low = priceLow ? parseInt(priceLow) : null;
    const high = priceType === "range" && priceHigh ? parseInt(priceHigh) : null;

    if (low !== null && low <= 0) {
      setError("Price must be a positive number.");
      return;
    }
    if (high !== null && low !== null && high <= low) {
      setError("Upper price must be higher than the lower price.");
      return;
    }

    setSubmitting(true);
    setError("");

    const result = await expressInterest(jobId, message.trim(), {
      amountLow: low,
      amountHigh: high,
      estimatedDuration: duration.trim() || null,
      earliestStartDate: startDate || null,
    });

    if (result.error) {
      setError(result.error);
      setSubmitting(false);
      return;
    }
    setSubmitted(true);
    setShowForm(false);
    setSubmitting(false);
  }

  if (submitted) {
    return (
      <div className="bg-success/10 border border-success/20 rounded-sm p-4 text-center">
        <p className="text-sm text-success font-medium">
          Your quote has been submitted. The homeowner will be in touch if they would like to proceed.
        </p>
      </div>
    );
  }

  if (!showForm) {
    return (
      <button
        onClick={() => {
          setTomorrow(new Date(Date.now() + 86400000).toISOString().split("T")[0]);
          setShowForm(true);
        }}
        className="w-full px-6 py-3 bg-forest text-white font-semibold rounded-sm hover:bg-forest-light transition-colors text-sm"
      >
        Submit a Quote
      </button>
    );
  }

  return (
    <div className="border border-forest/20 rounded-sm p-4 sm:p-6">
      <h3 className="font-serif font-bold text-charcoal text-lg mb-1">
        Submit your quote
      </h3>
      <p className="text-xs text-text-muted mb-5">
        Provide your price estimate, availability, and a message to stand out.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-sm text-xs text-error">
          {error}
        </div>
      )}

      {/* Price estimate */}
      <fieldset className="mb-4">
        <legend className="text-sm font-semibold text-text-body mb-2">
          Price estimate
        </legend>
        <div className="flex gap-3 mb-3">
          <button
            type="button"
            onClick={() => setPriceType("fixed")}
            className={`px-4 py-2.5 text-sm font-semibold rounded-sm border transition-colors ${
              priceType === "fixed"
                ? "bg-forest text-white border-forest"
                : "bg-white text-text-body border-border hover:border-forest"
            }`}
          >
            Fixed price
          </button>
          <button
            type="button"
            onClick={() => setPriceType("range")}
            className={`px-4 py-2.5 text-sm font-semibold rounded-sm border transition-colors ${
              priceType === "range"
                ? "bg-forest text-white border-forest"
                : "bg-white text-text-body border-border hover:border-forest"
            }`}
          >
            Price range
          </button>
        </div>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm">£</span>
            <input
              type="number"
              min="1"
              value={priceLow}
              onChange={(e) => setPriceLow(e.target.value)}
              placeholder={priceType === "fixed" ? "e.g. 350" : "From"}
              className="w-full pl-7 pr-3 py-2.5 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest"
            />
          </div>
          {priceType === "range" && (
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm">£</span>
              <input
                type="number"
                min="1"
                value={priceHigh}
                onChange={(e) => setPriceHigh(e.target.value)}
                placeholder="To"
                className="w-full pl-7 pr-3 py-2.5 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest"
              />
            </div>
          )}
        </div>
        <p className="text-[11px] text-text-muted mt-1.5">Optional. Leave blank if you need to see the job first.</p>
      </fieldset>

      {/* Duration + Start date */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-sm font-semibold text-text-body mb-2 block">
            Estimated duration
          </label>
          <input
            type="text"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="e.g. 2-3 days"
            className="w-full px-3 py-2.5 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest"
          />
          <p className="text-[11px] text-text-muted mt-1.5">Optional</p>
        </div>
        <div>
          <label className="text-sm font-semibold text-text-body mb-2 block">
            Earliest start date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            min={tomorrow}
            className="w-full px-3 py-2.5 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest"
          />
          <p className="text-[11px] text-text-muted mt-1.5">Optional</p>
        </div>
      </div>

      {/* Cover message */}
      <div className="mb-4">
        <label className="text-sm font-semibold text-text-body mb-2 block">
          Cover message
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          placeholder="Why are you a good fit? Mention relevant experience, qualifications, or anything the homeowner should know."
          className="w-full px-4 py-3 border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest resize-none"
        />
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="flex-1 px-6 py-3 bg-forest text-white font-semibold rounded-sm hover:bg-forest-light transition-colors text-sm disabled:opacity-50"
        >
          {submitting ? "Submitting..." : "Submit Quote"}
        </button>
        <button
          onClick={() => setShowForm(false)}
          className="px-4 py-3 text-sm text-text-muted hover:text-text-body transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
